import { DataTable } from "@/components/molecules/tables/data-table";
import { useCustomers } from "@/features/customers/hooks/use-customers";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { PageLayout } from "../layout";
import { useCustomersBulkActions } from "./components/bulk-actions";
import { useCustomersColumns } from "./components/columns";
import { CreateCustomerDialog } from "./components/create-dialog";
import { useTranslation } from "@/features/utils/i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function ContactsPage() {
  const { t } = useTranslation("customers");
  const { company: clientId } = useParams<{ company: string }>();
  const columns = useCustomersColumns();
  const bulkActions = useCustomersBulkActions();
  const [search, setSearch] = useState("");
  // Unknown contacts (created from an inbound call, without any name) are hidden by default
  const [hideUnknown, setHideUnknown] = useState(true);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_at", desc: true },
  ]);

  // Handle search with automatic filter extraction
  const handleSearchChange = (
    searchText: string,
    extractedFilters?: Record<string, unknown>,
  ) => {
    setSearch(searchText);

    // Merge extracted filters
    if (extractedFilters && Object.keys(extractedFilters).length > 0) {
      setFilters((prev) => {
        const newFilters = { ...prev };

        Object.entries(extractedFilters).forEach(([key, value]) => {
          if (
            typeof value === "object" &&
            value !== null &&
            "operator" in value &&
            "value" in value
          ) {
            const filterValue = (value as { operator: string; value: unknown })
              .value;
            if ((value as { operator: string }).operator === "eq") {
              newFilters[key] = filterValue;
            }
          } else {
            newFilters[key] = value;
          }
        });

        return newFilters;
      });
    }
  };

  const { data, isLoading } = useCustomers(clientId || "", {
    ...filters,
    search: search || undefined,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
    hide_unknown: hideUnknown,
  });

  const customers = data?.customers || [];
  const total = data?.total || 0;

  return (
    <PageLayout
      title={t("title")}
      description={t("description")}
      actions={<CreateCustomerDialog />}
    >
      <DataTable
        columns={columns}
        data={customers}
        isLoading={isLoading}
        total={total}
        pageCount={Math.ceil(total / pagination.pageSize)}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        globalFilter={search}
        setGlobalFilter={handleSearchChange}
        bulkActions={bulkActions}
        onRowNavigate={(customer) => `/${clientId}/contacts/${customer.id}`}
        searchBar={(searchBar) => (
          <div className="flex flex-1 flex-wrap gap-2">
            {searchBar}
            <Select
              value={hideUnknown ? "named" : "all"}
              onValueChange={(value) => {
                setHideUnknown(value === "named");
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t("filters.unknown.label")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="named">
                  {t("filters.unknown.named")}
                </SelectItem>
                <SelectItem value="all">{t("filters.unknown.all")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      />
    </PageLayout>
  );
}
